import React, { useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { AccessibilityProvider } from './components/AccessibilityProvider';
import AccessibilityToolbar from './components/AccessibilityToolbar';
import { SearchProvider } from './context/SearchContext';
import './i18n/config';

// Public Pages
import HomePage from './pages/HomePage';
import ServicesPage from './pages/ServicesPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import ContactPage from './pages/ContactPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Imprint from './pages/Imprint';
import NotFoundPage from './pages/NotFoundPage';

// Admin Pages
import AdminLogin from './admin/AdminLogin';
import AdminDashboard from './admin/AdminDashboard';
import AdminServices from './admin/AdminServices';
import AdminTestimonials from './admin/AdminTestimonials';
import AdminBlog from './admin/AdminBlog';
import AdminContent from './admin/AdminContent';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import GoogleAnalytics from './components/GoogleAnalytics';
import GlobalSearch from './components/GlobalSearch';

function App() {
  const { i18n } = useTranslation();

  // Update HTML lang attribute based on current language
  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <HelmetProvider>
      <AccessibilityProvider>
        <SearchProvider>
          <GoogleAnalytics trackingId={process.env.REACT_APP_GA_TRACKING_ID} />
          <div className="App">
          <Routes>
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/*" element={
              <ProtectedRoute>
                <AdminRoutes />
              </ProtectedRoute>
            } />
            
            {/* Public Routes */}
            <Route path="/*" element={<PublicRoutes />} />
          </Routes>
          <AccessibilityToolbar />
          <GlobalSearch />
          </div>
        </SearchProvider>
      </AccessibilityProvider>
    </HelmetProvider>
  );
}

const PublicRoutes = () => (
  <>
    <a href="#main-content" className="skip-link">
      Skip to main content
    </a>
    <Header />
    <main id="main-content" style={{ minHeight: 'calc(100vh - 160px)' }}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/imprint" element={<Imprint />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </main>
    <Footer />
  </>
);

const AdminRoutes = () => (
  <Routes>
    <Route path="/" element={<AdminDashboard />} />
    <Route path="/services" element={<AdminServices />} />
    <Route path="/testimonials" element={<AdminTestimonials />} />
    <Route path="/blog" element={<AdminBlog />} />
    <Route path="/content" element={<AdminContent />} />
  </Routes>
);

export default App;